import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import useHttp from './useHttp';

const usePlayerNameBlacklist = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest, setError } = useHttp();

  const request = useCallback(
    async ({ url, method, body }, { errorToast } = {}) => {
      try {
        return await sendRequest({
          url,
          method,
          headers: authCtx.getAuthHeaders(),
          body,
        });
      } catch (e) {
        if (errorToast) toast.error(errorToast);
        throw e;
      }
    },
    [authCtx, sendRequest]
  );

  const getEntries = useCallback(async () => {
    return request(
      { url: '/api/v1/playerNameBlacklist', method: 'GET' },
      { errorToast: 'Fehler beim Laden der Blacklist' }
    );
  }, [request]);

  const createEntry = useCallback(
    async ({ term, matchWholeWordOnly = false } = {}) => {
      return request({
        url: '/api/v1/playerNameBlacklist',
        method: 'POST',
        body: { term, matchWholeWordOnly: matchWholeWordOnly === true },
      });
    },
    [request]
  );

  const updateEntry = useCallback(
    async (entryId, patch) => {
      return request(
        { url: `/api/v1/playerNameBlacklist/${entryId}`, method: 'PATCH', body: patch },
        { errorToast: 'Fehler beim Speichern' }
      );
    },
    [request]
  );

  const deleteEntry = useCallback(
    async entryId => {
      return request(
        { url: `/api/v1/playerNameBlacklist/${entryId}`, method: 'DELETE' },
        { errorToast: 'Fehler beim Löschen' }
      );
    },
    [request]
  );

  const getSuggestions = useCallback(
    async ({ status } = {}) => {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      return request(
        { url: `/api/v1/playerNameBlacklist/suggestions${qs}`, method: 'GET' },
        { errorToast: 'Fehler beim Laden der Vorschläge' }
      );
    },
    [request]
  );

  const createSuggestion = useCallback(
    async ({ term, matchWholeWordOnly = false } = {}) => {
      return request({
        url: '/api/v1/playerNameBlacklist/suggestions',
        method: 'POST',
        body: { term, matchWholeWordOnly: matchWholeWordOnly === true },
      });
    },
    [request]
  );

  const createEditSuggestion = useCallback(
    async ({ oldPlayerName, branchId, memberId, bookingId }) => {
      return request({
        url: '/api/v1/playerNameBlacklist/edit-suggestions',
        method: 'POST',
        body: { oldPlayerName, branchId, memberId, bookingId },
      });
    },
    [request]
  );

  const getEditSuggestions = useCallback(
    async ({ status } = {}) => {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      return request({
        url: `/api/v1/playerNameBlacklist/edit-suggestions${qs}`,
        method: 'GET',
      });
    },
    [request]
  );

  const markEditSuggestionHandled = useCallback(
    async ({ editSuggestionId, handledComment, status = 'handled' } = {}) => {
      const resolvedStatus = status === 'dismissed' ? 'dismissed' : 'handled';
      return request({
        url: `/api/v1/playerNameBlacklist/edit-suggestions/${editSuggestionId}`,
        method: 'PATCH',
        body: { status: resolvedStatus, ...(handledComment ? { handledComment } : {}) },
      });
    },
    [request]
  );

  const updateSuggestion = useCallback(
    async (suggestionId, { term, matchWholeWordOnly = false }) => {
      return request({
        url: `/api/v1/playerNameBlacklist/suggestions/${suggestionId}`,
        method: 'PATCH',
        body: { term, matchWholeWordOnly: matchWholeWordOnly === true },
      });
    },
    [request]
  );

  const deleteSuggestion = useCallback(
    async suggestionId => {
      return request({
        url: `/api/v1/playerNameBlacklist/suggestions/${suggestionId}`,
        method: 'DELETE',
      });
    },
    [request]
  );

  const approveSuggestion = useCallback(
    async suggestionId => {
      return request({ url: `/api/v1/playerNameBlacklist/suggestions/${suggestionId}/approve`, method: 'POST' });
    },
    [request]
  );

  const rejectSuggestion = useCallback(
    async (suggestionId, reason) => {
      return request({
        url: `/api/v1/playerNameBlacklist/suggestions/${suggestionId}/reject`,
        method: 'POST',
        body: reason ? { reason } : undefined,
      });
    },
    [request]
  );

  return {
    isLoading,
    error,
    setError,
    getEntries,
    createEntry,
    updateEntry,
    deleteEntry,
    getEditSuggestions,
    createEditSuggestion,
    markEditSuggestionHandled,
    getSuggestions,
    createSuggestion,
    updateSuggestion,
    deleteSuggestion,
    approveSuggestion,
    rejectSuggestion,
  };
};

export default usePlayerNameBlacklist;

